"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useDragLayer, useDrop } from "react-dnd";
import { ISpecificationCategoryResponse } from "@/shared/services/interface/responses/ISpecificationResponse";
import { getTargetMetaAtPoint, type TargetMeta } from "./dom-extraction";

export type DragIntent =
  | {
      type: "REORDER";
      parentId: string | null;
      targetId: string;
      position: "before" | "after";
      targetNextNodeId?: string | null;
      targetPrevNodeId?: string | null;
    }
  | { type: "MAKE_CHILD"; parentId: string };

export interface DraggedNode {
  id: string;
  nodeType: "ITEM" | "CATEGORY";
  parentId: string | null;
}

interface TreeDnDContextValue {
  previewIntent: DragIntent | null;
  draggedItem: DraggedNode | null;
  getPreviewClass: (nodeId: string) => string | undefined;
}

const TreeDnDContext = createContext<TreeDnDContextValue | null>(null);

export const DND_TYPE = "TREE_NODE" as const;
const POSITION_THRESHOLD = 15; // 10px threshold for before/after detection
const THROTTLE_MS = 16;

interface TreeDnDProviderProps {
  children: React.ReactNode;
  categories: ISpecificationCategoryResponse[];
  handleDrop: (intent: DragIntent, draggedItem: DraggedNode) => void;
}

export function TreeDnDProvider({
  children,
  categories,
  handleDrop,
}: TreeDnDProviderProps) {
  const [previewIntent, setPreviewIntent] = useState<DragIntent | null>(null);
  const previewIntentRef = useRef<DragIntent | null>(null);
  const categoriesRef = useRef<ISpecificationCategoryResponse[]>(categories);
  const lastProcessedTimeRef = useRef<number>(0);
  const { clientOffset, isDragging, draggedItem } = useDragLayer((monitor) => ({
    clientOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
    draggedItem: monitor.getItem() as DraggedNode | null,
  }));

  useEffect(() => {
    if (!isDragging) {
      setPreviewIntent(null);
      previewIntentRef.current = null;
      lastProcessedTimeRef.current = 0;
    }
  }, [isDragging]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  /**
   * Pure function that calculates drag intent from target metadata.
   *
   * SIMPLE LOGIC (only based on Y position):
   * - 10px üstüne gittiyse → REORDER before
   * - 10px altına gittiyse → REORDER after
   * - Ortasındaysa (10px ile height-10px arası) → MAKE_CHILD
   */
  const calculateIntentPure = useCallback(
    (params: {
      dragged: DraggedNode;
      point: { x: number; y: number };
      target: TargetMeta;
    }): DragIntent | null => {
      const { dragged, point, target } = params;

      // Cannot drop on itself
      if (target.targetId === dragged.id) return null;

      // Calculate position relative to target element's top
      const relativeY = point.y - target.targetRect.top;

      // 10px üstüne gittiyse → REORDER before
      if (relativeY < 8) {
        return {
          type: "REORDER",
          parentId: target.targetParentId,
          targetId: target.targetId,
          targetNextNodeId: target.targetNextNodeId,
          targetPrevNodeId: target.targetPrevNodeId,
          position: "before",
        };
      }

      // 10px altına gittiyse → REORDER after
      if (relativeY > target.targetRect.height - POSITION_THRESHOLD) {
        return {
          type: "REORDER",
          parentId: target.targetParentId,
          targetId: target.targetId,
          targetNextNodeId: target.targetNextNodeId,
          targetPrevNodeId: target.targetPrevNodeId,
          position: "after",
        };
      }
      if (dragged.nodeType === "ITEM" && target.targetType === "ITEM") {
        return null;
      }
      if (dragged.parentId === target.targetId) {
        return null;
      }
      // Ortasındaysa (10px ile height-10px arası) → MAKE_CHILD
      return { type: "MAKE_CHILD", parentId: target.targetId };
    },
    []
  );

  const calculateIntent = useCallback(
    (
      clientX: number,
      clientY: number,
      draggedNode: DraggedNode
    ): DragIntent | null => {
      // Extract target node metadata from DOM
      const target = getTargetMetaAtPoint(clientX, clientY);
      if (!target) return null;

      // Calculate intent using pure function
      return calculateIntentPure({
        dragged: draggedNode,
        point: { x: clientX, y: clientY },
        target,
      });
    },
    [calculateIntentPure]
  );

  useEffect(() => {
    if (!isDragging || !clientOffset || !draggedItem) {
      setPreviewIntent(null);
      previewIntentRef.current = null;
      return;
    }

    const now = Date.now();
    if (now - lastProcessedTimeRef.current < THROTTLE_MS) {
      return;
    }
    lastProcessedTimeRef.current = now;

    const newIntent = calculateIntent(
      clientOffset.x,
      clientOffset.y,
      draggedItem
    );

    if (
      JSON.stringify(newIntent) !== JSON.stringify(previewIntentRef.current)
    ) {
      setPreviewIntent(newIntent);
      previewIntentRef.current = newIntent;
    }
  }, [clientOffset, isDragging, draggedItem, calculateIntent]);

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: [DND_TYPE],
      drop: (item, monitor) => {
        const currentPreviewIntent = previewIntentRef.current;
        const currentDraggedNode = monitor.getItem() as DraggedNode | null;

        if (!currentPreviewIntent || !currentDraggedNode) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Drop failed: No preview intent");
          }
          return;
        }
        handleDrop(currentPreviewIntent, currentDraggedNode);
        setPreviewIntent(null);
        previewIntentRef.current = null;
        lastProcessedTimeRef.current = 0;
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    []
  );

  const getPreviewClass = useCallback(
    (nodeId: string): string | undefined => {
      if (!previewIntent) return undefined;

      if (previewIntent.type === "MAKE_CHILD") {
        if (previewIntent.parentId === nodeId) {
          return "dnd-make-child";
        }
      } else if (previewIntent.type === "REORDER") {
        if (previewIntent.targetId === nodeId) {
          return previewIntent.position === "before"
            ? "dnd-insert-before"
            : "dnd-insert-after";
        }
      }

      return undefined;
    },
    [previewIntent]
  );

  const value: TreeDnDContextValue = useMemo(
    () => ({
      previewIntent,
      draggedItem,
      getPreviewClass,
    }),
    [previewIntent, draggedItem, getPreviewClass]
  );

  return (
    <TreeDnDContext.Provider value={value}>
      <div ref={dropRef} className="h-full w-full">
        {children}
      </div>
    </TreeDnDContext.Provider>
  );
}

export { TreeDnDContext };
